// /Users/rd/CaseManageVue/src/composables/useUsers.js

import { ref, computed } from 'vue'
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore'
import { db } from '../firebase'
import { devLog } from '@/utils/environment'
import { useSchoolStore } from '@/store/schoolStore'
import { useAppSettings } from '@/composables/useAppSettings'
import { TEACHER_DROPDOWN_ROLES } from '@/config/roles'

// ─── School-scoped user reads (district tenancy) ─────────────────────────────
// The users collection scales with the WHOLE district (20+ schools), so page
// loads must not scan it. School-scoped roles read only the active school's
// users (schoolIds array-contains); district-level roles keep the full
// directory. firestore.rules enforces the same perimeter on list queries
// (userInScopeForList), so an unscoped list from a school-scoped token would
// be denied server-side anyway.

/**
 * Decide whether a users list should be school-scoped.
 * District-tier `fetchUsers()` stays unscoped unless the admin panel or a
 * school page (`/students`, caseload, meeting calendar) opts in. School-tier
 * roles always scope when a campus is selected.
 *
 * @param {{ currentSchoolId?: string|null, isDistrictLevel?: boolean, adminSchoolScopeActive?: boolean, forceSchoolPage?: boolean }} args
 * @returns {{ scopeKey: string, scoped: boolean }}
 */
export function resolveUsersQueryScope({
  currentSchoolId = null,
  isDistrictLevel = false,
  adminSchoolScopeActive = false,
  forceSchoolPage = false
} = {}) {
  if (currentSchoolId && (forceSchoolPage || adminSchoolScopeActive || !isDistrictLevel)) {
    return { scopeKey: currentSchoolId, scoped: true }
  }
  return { scopeKey: '__all__', scoped: false }
}

/**
 * Resolve the users query for the current context: scoped to the active school
 * unless the caller is district-level or no school registry exists (single
 * pre-migration projects keep the original full read).
 *
 * Shared by useUsers and admin/debug tools so every reader applies the same
 * perimeter the rules enforce.
 *
 * @returns {Promise<{queryRef: import('firebase/firestore').Query, scopeKey: string}>}
 */
// While the school Admin panel is managing a specific school, district-level
// roles should also read only that school's users (their default directory
// read is intentionally unscoped). AdminView toggles this for its lifetime, so
// every fetchUsers() from any admin tab scopes without editing each call site.
// District views (dashboard/directory) never set it and stay unscoped.
const adminSchoolScopeActive = ref(false)

/** Enable/disable admin-panel school scoping for district-level roles. */
export function setAdminSchoolScope(active) {
  adminSchoolScopeActive.value = !!active
}

async function usersQueryFromScope({ forceSchoolPage = false } = {}) {
  try {
    const schoolStore = useSchoolStore()
    await schoolStore.loadSchools()
    const decided = resolveUsersQueryScope({
      currentSchoolId: schoolStore.currentSchoolId,
      isDistrictLevel: schoolStore.isDistrictLevel,
      adminSchoolScopeActive: adminSchoolScopeActive.value,
      forceSchoolPage
    })
    if (decided.scoped) {
      return {
        queryRef: query(
          collection(db, 'users'),
          where('schoolIds', 'array-contains', decided.scopeKey)
        ),
        scopeKey: decided.scopeKey
      }
    }
  } catch {
    /* Pinia not active (unit tests) → unscoped */
  }
  return { queryRef: collection(db, 'users'), scopeKey: '__all__' }
}

export async function usersQueryForActiveSchool() {
  return usersQueryFromScope()
}

/** School pages (`/students`) — always scope when a campus is selected. */
export async function usersQueryForSchoolPage() {
  return usersQueryFromScope({ forceSchoolPage: true })
}

/** getDocs() with the school perimeter applied — drop-in for admin/debug tools. */
export async function getScopedUsersSnapshot() {
  const { queryRef } = await usersQueryForActiveSchool()
  return getDocs(queryRef)
}

/**
 * Always load users for one school (`schoolIds` array-contains).
 * Use this for school-admin tools (schedule import teacher matching, etc.)
 * even when the signed-in user is district-level — their default
 * `fetchUsers()` is intentionally unscoped for district directories.
 *
 * @param {string|null} schoolId
 * @returns {Promise<{ map: Record<string, object>, list: object[] }>}
 */
export async function fetchUsersMapForSchool(schoolId) {
  const queryRef = schoolId
    ? query(collection(db, 'users'), where('schoolIds', 'array-contains', schoolId))
    : collection(db, 'users')
  const snap = await getDocs(queryRef)
  const map = {}
  const list = []
  snap.forEach((d) => {
    const user = { id: d.id, ...d.data() }
    map[d.id] = user
    list.push(user)
  })
  return { map, list }
}

/**
 * Fetch the users of several schools (one array-contains query per school,
 * merged and de-duplicated). Used where a school-scoped admin needs users
 * across all of THEIR schools (district directory, pending approvals) — an
 * unscoped list would be denied by the rules perimeter.
 *
 * @param {string[]} schoolIds
 * @returns {Promise<Array<Object>>}
 */
export async function fetchUsersForSchools(schoolIds = []) {
  const ids = [...new Set(schoolIds.filter(Boolean))]
  const snaps = await Promise.all(
    ids.map((sid) =>
      getDocs(query(collection(db, 'users'), where('schoolIds', 'array-contains', sid)))
    )
  )
  const byId = new Map()
  for (const snap of snaps) {
    snap.forEach((d) => {
      if (!byId.has(d.id)) byId.set(d.id, { id: d.id, ...d.data() })
    })
  }
  return [...byId.values()]
}

// Per-scope cache so concurrent mounts (AdminView + AdminDashboard + students
// page) collapse into one network read, and switching back to an already-loaded
// school is instant. Short TTL keeps admin edits from going stale for long.
const USERS_CACHE_TTL_MS = 60_000
const usersCacheByScope = new Map() // scopeKey -> { ts, promise }

export function clearUsersCache() {
  usersCacheByScope.clear()
}

// On-demand single-user resolution for ids referenced by students but outside
// the active school's map (cross-school co-teachers, district-level providers).
// Module-level so the getDoc runs once per id across all composable instances.
const resolvedUserCache = new Map() // id -> user object | null (null = known miss)
const resolveInFlight = new Map() // id -> Promise

// Reactive set of ids confirmed missing from Firestore (deleted users, etc.)
export const knownMissingUserIds = ref(new Set())

function markKnownMissing(userId) {
  if (!userId || knownMissingUserIds.value.has(userId)) return
  const next = new Set(knownMissingUserIds.value)
  next.add(userId)
  knownMissingUserIds.value = next
}

async function fetchUserDoc(userId) {
  if (resolvedUserCache.has(userId)) return resolvedUserCache.get(userId)
  if (!resolveInFlight.has(userId)) {
    resolveInFlight.set(
      userId,
      getDoc(doc(db, 'users', userId))
        .then((snap) => (snap.exists() ? { id: snap.id, ...snap.data() } : null))
        .catch(() => null)
        .then((user) => {
          resolvedUserCache.set(userId, user)
          if (!user) markKnownMissing(userId)
          resolveInFlight.delete(userId)
          return user
        })
    )
  }
  return resolveInFlight.get(userId)
}

/**
 * Seed users loaded by another query (e.g. district case managers) into the
 * shared name map and the single-doc cache. Not added to `userList`, so
 * school-scoped dropdowns are unchanged.
 */
export function mergeKnownUsers(list = []) {
  const additions = {}
  for (const user of list || []) {
    if (!user?.id) continue
    resolvedUserCache.set(user.id, user)
    if (!users.value[user.id]) additions[user.id] = user
  }
  if (Object.keys(additions).length) users.value = { ...users.value, ...additions }
}

/** Fire-and-forget resolve shared across composable instances. */
export function resolveUserById(userId) {
  if (!userId) return Promise.resolve(null)
  return fetchUserDoc(userId).then((user) => {
    if (user && !users.value[userId]) {
      users.value = { ...users.value, [userId]: user }
    }
    return user
  })
}

// Shared reactive state so every useUsers() caller sees the same map
// (including users resolved on demand for deleted-id vs cross-school checks).
const users = ref({})
const userList = ref([])

export default function useUsers() {
  // Called here (setup context) rather than inside the userRoles computed —
  // useAppSettings registers a school-switch watch that must not be created
  // from a computed getter. appSettings is a shared module-level ref.
  const { appSettings } = useAppSettings()

  async function loadUsers(getQuery) {
    const { queryRef, scopeKey } = await getQuery()

    const cached = usersCacheByScope.get(scopeKey)
    if (!cached || Date.now() - cached.ts > USERS_CACHE_TTL_MS) {
      const promise = getDocs(queryRef)
      usersCacheByScope.set(scopeKey, { ts: Date.now(), promise })
      // A failed read must not poison the cache for the TTL window.
      promise.catch(() => usersCacheByScope.delete(scopeKey))
    }
    const userSnap = await usersCacheByScope.get(scopeKey).promise
    devLog('🔍 USERS DEBUG: Retrieved', userSnap.size, 'users (scope:', scopeKey, ')')

    const userMap = {}
    const allUsers = []
    userSnap.forEach((docSnap) => {
      const data = docSnap.data()
      userMap[docSnap.id] = { id: docSnap.id, ...data }
      allUsers.push({ id: docSnap.id, ...data })
    })
    // Out-of-scope users resolved or seeded earlier keep their names.
    for (const [id, user] of resolvedUserCache) {
      if (user && !userMap[id]) userMap[id] = user
    }

    users.value = userMap
    userList.value = allUsers
  }

  async function fetchUsers() {
    devLog('🔍 USERS DEBUG: Starting fetchUsers()')
    try {
      await loadUsers(usersQueryForActiveSchool)
      devLog('🔍 USERS DEBUG: fetchUsers() completed successfully')
    } catch (error) {
      console.error('🔴 USERS DEBUG: Error in fetchUsers():', error)
      throw error
    }
  }

  // /students and other school pages. District-tier fetchUsers() is unscoped
  // on purpose (directories). A campus click-through must not list every
  // staff member in the district. Out-of-school names still resolve via
  // resolveUser (get-by-id, not added to dropdown lists).
  async function fetchUsersForSchoolPage() {
    devLog('🔍 USERS DEBUG: Starting fetchUsersForSchoolPage()')
    try {
      await loadUsers(usersQueryForSchoolPage)
      devLog('🔍 USERS DEBUG: fetchUsersForSchoolPage() completed successfully')
    } catch (error) {
      console.error('🔴 USERS DEBUG: Error in fetchUsersForSchoolPage():', error)
      throw error
    }
  }

  /**
   * Resolve a user id that is missing from the school-scoped map (e.g. an
   * out-of-school co-teaching case manager). Merges the result into `users`
   * for reactive name display, but NOT into `userList` — dropdowns stay
   * school-scoped. Fire-and-forget safe.
   */
  async function resolveUser(userId) {
    if (!userId) return null
    if (users.value[userId]) return users.value[userId]
    const user = await fetchUserDoc(userId)
    if (user && !users.value[userId]) {
      users.value = { ...users.value, [userId]: user }
    } else if (!user) {
      markKnownMissing(userId)
    }
    return user
  }

  const userRoles = computed(() => {
    const sortByLastName = (users) =>
      users.sort((a, b) => {
        // Extract last names for sorting
        const getLastName = (user) => {
          const fullName = user.name || user.email || user.id
          const nameParts = fullName.split(' ')
          return nameParts.length > 1 ? nameParts[nameParts.length - 1] : fullName
        }
        return getLastName(a).localeCompare(getLastName(b))
      })

    const roles = {
      teachers: sortByLastName(
        userList.value.filter((u) => TEACHER_DROPDOWN_ROLES.includes(u.role) || u.showInTeacherList)
      ),
      caseManagers: sortByLastName(
        userList.value.filter((u) =>
          [
            'case_manager',
            'case_manager_full',
            'sped_chair',
            'sped_chair_all',
            'admin_504',
            'speech_case_manager'
          ].includes(u.role)
        )
      ),
      speech: sortByLastName(
        userList.value.filter((u) => u.provider === 'SLP' || u.role === 'speech_case_manager')
      ),
      ot: sortByLastName(userList.value.filter((u) => u.provider === 'OT')),
      mh: sortByLastName(userList.value.filter((u) => u.provider === 'MH')),
      cmh: sortByLastName(userList.value.filter((u) => u.provider === 'CMH')),
      pt: sortByLastName(userList.value.filter((u) => u.provider === 'PT')),
      sc: sortByLastName(userList.value.filter((u) => u.provider === 'SC')),
      tr: sortByLastName(userList.value.filter((u) => u.provider === 'TR')),
      aud: sortByLastName(userList.value.filter((u) => u.provider === 'AUD')),
      vi: sortByLastName(userList.value.filter((u) => u.provider === 'VI')),
      at: sortByLastName(userList.value.filter((u) => u.provider === 'AT')),
      dhh: sortByLastName(userList.value.filter((u) => u.provider === 'DHH')),
      om: sortByLastName(userList.value.filter((u) => u.provider === 'O&M')),
      ap: sortByLastName(userList.value.filter((u) => u.provider === 'AP')),
      psych: sortByLastName(
        userList.value.filter(
          (u) => u.provider === 'PSYCH' || u.provider === 'PSYCH2' || u.role === 'school_psych'
        )
      ),
      bis: sortByLastName(userList.value.filter((u) => u.provider === 'BIS')),
      hn: sortByLastName(userList.value.filter((u) => u.provider === 'HN')),
      sw: sortByLastName(userList.value.filter((u) => u.provider === 'SW')),
      aut: sortByLastName(userList.value.filter((u) => u.provider === 'AUT'))
    }

    // District/school-defined custom provider types: users whose `provider`
    // matches the custom abbreviation. Keyed by lowercase abbreviation — the
    // same lookup getProviderUsers() falls back to in useStudentForm.
    for (const p of appSettings.value?.customServiceProviders || []) {
      const key = (p?.abbreviation || '').toLowerCase()
      if (key && !roles[key]) {
        roles[key] = sortByLastName(userList.value.filter((u) => u.provider === p.abbreviation))
      }
    }

    return roles
  })

  const caseManagers = computed(() => {
    return userList.value
      .filter((u) =>
        [
          'case_manager',
          'case_manager_full',
          'sped_chair',
          'sped_chair_all',
          'admin_504',
          'speech_case_manager'
        ].includes(u.role)
      )
      .sort((a, b) => {
        // Extract last names for sorting
        const getLastName = (user) => {
          const fullName = user.name || user.email || user.id
          const nameParts = fullName.split(' ')
          return nameParts.length > 1 ? nameParts[nameParts.length - 1] : fullName
        }
        return getLastName(a).localeCompare(getLastName(b))
      })
  })

  const teacherList = computed(() => {
    return userList.value
      .filter((u) => TEACHER_DROPDOWN_ROLES.includes(u.role) || u.showInTeacherList)
      .sort((a, b) => {
        // Extract last names for sorting
        const getLastName = (user) => {
          const fullName = user.name || user.email || user.id
          const nameParts = fullName.split(' ')
          return nameParts.length > 1 ? nameParts[nameParts.length - 1] : fullName
        }
        return getLastName(a).localeCompare(getLastName(b))
      })
  })

  return {
    users,
    userList,
    fetchUsers,
    fetchUsersForSchoolPage,
    resolveUser,
    caseManagers,
    teacherList,
    userRoles
  }
}
